// PromotionalActivity.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Label,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import { Edit, Trash2, Copy } from "react-feather";
import { FaPlus, FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import swal from "sweetalert";

/* ===== shared utilities in ../import (no AG Grid, no SCSS) ===== */
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { convertDataCSVtoExcel, exportDataToPDF } from "../import/Downloader";

/* ===== APIs ===== */
import {
  _Get,
  _PostSave,
  _Put,
  _Delete,
  View_PromotionList,
  ProductListView,
  SavePromotionsActivity,
} from "../import/ApiEndPoint/ApiCalling";
import {
  ActivityList,
  SaveActivity,
  Update_Activity,
  Delete_Activity,
  DeleteOne_Promotion,
  PurchaseProductList_Product,
} from "../import/ApiEndPoint/Api";

/* ---------------- helpers ---------------- */
const OMIT_KEYS = [
  "_id",
  "__v",
  "created_by",
  "createdAt",
  "updatedAt",
  "status",
  "activityId",
  "promoCodeWise",
  "database",
];
const bucketCols = [];
const prettyDate = (iso = "") => (iso || "").split("T")[0] || "";

function makePromoColumns({ permissions, onEdit, onDelete }) {
  return [
    {
      id: "actions",
      headerName: "Actions",
      width: 110,
      cell: (row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          {permissions?.Edit && (
            <Edit
              size={18}
              color="green"
              style={{ cursor: "pointer" }}
              onClick={() => onEdit(row)}
              title="Edit"
            />
          )}
          {permissions?.Delete && (
            <Trash2
              size={18}
              color="red"
              style={{ cursor: "pointer" }}
              onClick={() => onDelete(row)}
              title="Delete"
            />
          )}
        </div>
      ),
    },
    { id: "status", headerName: "Status", accessor: (r) => r?.status || "" },
    { id: "Code", headerName: "Code", accessor: (r) => r?.Code || "" },
    {
      id: "ActivityName",
      headerName: "Activity",
      accessor: (r) => r?.ActivityName || "",
    },
    {
      id: "FromDate",
      headerName: "Start",
      accessor: (r) => prettyDate(r?.FromDate),
    },
    { id: "ToDate", headerName: "End", accessor: (r) => prettyDate(r?.ToDate) },
    { id: "NoOfDays", headerName: "Days", accessor: (r) => r?.NoOfDays ?? "" },
    {
      id: "Product.Product_Title",
      headerName: "Product",
      accessor: (r) => r?.Product?.Product_Title || "",
      width: 260,
    },
    {
      id: "target",
      headerName: "Target",
      accessor: (r) =>
        (r?.totalAmount > 0 ? r?.totalAmount : r?.targetQty) ?? "",
      width: 120,
    },
    {
      id: "discountPct",
      headerName: "Disc %",
      accessor: (r) =>
        r?.percentageDiscount > 0
          ? r?.percentageDiscount
          : r?.discountPercentage ?? "",
      width: 110,
    },
    {
      id: "discountAmount",
      headerName: "Disc ₹",
      accessor: (r) =>
        r?.percentageAmount > 0 ? r?.percentageAmount : r?.discountAmount ?? "",
      width: 110,
    },
    {
      id: "freeProductQty",
      headerName: "Free Qty",
      accessor: (r) => r?.freeProductQty ?? "",
      width: 110,
    },
    {
      id: "freeProductName.Product_Title",
      headerName: "Free Product",
      accessor: (r) => r?.freeProductName?.Product_Title || "",
      width: 260,
    },
  ];
}

function rowsToCSV(rows, columns) {
  const safe = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const headers = columns
    .filter((c) => c.id !== "actions")
    .map((c) => safe(c.headerName))
    .join(",");
  const body = rows
    .map((row) =>
      columns
        .filter((c) => c.id !== "actions")
        .map((c) => (c.accessor ? safe(c.accessor(row)) : safe(row?.[c.id])))
        .join(",")
    )
    .join("\n");
  return `${headers}\n${body}`;
}
function downloadCSV(csv, filename = "PromotionalActivity.csv") {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ---------------- component ---------------- */
export default function PromotionalActivity() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("promo"); // "promo" | "activity"
  const [permissions, setPermissions] = useState({});
  const [masterShow, setMasterShow] = useState(false);
  const [loading, setLoading] = useState(false);

  /* Activities */
  const [activityList, setActivityList] = useState([]);
  const [activitySearch, setActivitySearch] = useState("");
  const filteredActivities = useMemo(() => {
    const s = activitySearch.toLowerCase();
    return activityList.filter(
      (a) =>
        (a.ActivityName || "").toLowerCase().includes(s) ||
        (a.CustomerName || "").toLowerCase().includes(s) ||
        (a.ActivityType || "").toLowerCase().includes(s)
    );
  }, [activityList, activitySearch]);

  const [actModal, setActModal] = useState(false);
  const toggleAct = () => setActModal((m) => !m);
  const [actForm, setActForm] = useState({
    ActivityName: "",
    ActivityType: "",
    FromDate: "",
    ToDate: "",
    status: "Active",
    id: null,
    editActivity: false,
  });

  /* Builder (left) */
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [discountType, setDiscountType] = useState("");
  const [promoStatus, setPromoStatus] = useState("Active");
  const [totalAmount, setTotalAmount] = useState("");
  const [percentOff, setPercentOff] = useState("");
  const [flatAmount, setFlatAmount] = useState("");
  const [productRows, setProductRows] = useState([
    {
      productId: "",
      targetQty: "",
      freeProduct: "",
      freeProductQty: "",
      discountPercentage: "",
      discountAmount: "",
    },
  ]);
  const addRow = () =>
    setProductRows((r) => [
      ...r,
      {
        productId: "",
        targetQty: "",
        freeProduct: "",
        freeProductQty: "",
        discountPercentage: "",
        discountAmount: "",
      },
    ]);
  const removeRow = (idx) =>
    setProductRows((r) => r.filter((_, i) => i !== idx));
  const onRowChange = (idx, key, value) =>
    setProductRows((r) =>
      r.map((x, i) => (i === idx ? { ...x, [key]: value } : x))
    );
  const onSelectProduct = (_sl, si, idx, field) =>
    onRowChange(idx, field, si?._id || "");
  const onRemoveProduct = (_sl, _si, idx, field) => onRowChange(idx, field, "");

  /* Data */
  const [productList, setProductList] = useState([]);
  const [allPromos, setAllPromos] = useState([]);

  /* Right list */
  const [promoType, setPromoType] = useState("NA");
  const [promoSearch, setPromoSearch] = useState("");
  const [promoRows, setPromoRows] = useState([]);
  const [visibleCols, setVisibleCols] = useState([]);
  const [allCols, setAllCols] = useState([]);
  const [focusIndex, setFocusIndex] = useState(-1);

  const [dlOpen, setDlOpen] = useState(false);
  const dlTimer = useRef(null);
  const onDownloadHover = (e) => {
    if (e.type === "mouseenter") {
      clearTimeout(dlTimer.current);
      setDlOpen(true);
    } else {
      dlTimer.current = setTimeout(() => setDlOpen(false), 180);
    }
  };

  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);
  const [promoTypeOptions, setPromoTypeOptions] = useState([]);

  /* init */
  useEffect(() => {
    const p = CheckPermission("Promotional Activity") || {};
    setPermissions(p);
    const user = JSON.parse(localStorage.getItem("userData"));
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
    if (user?.database) loadBootstrap(user?._id, user?.database);
  }, []);

  const loadBootstrap = async (id, db) => {
    setLoading(true);
    try {
      const a = await _Get(ActivityList, db).catch(() => ({ Activity: [] }));
      setActivityList(a?.Activity || []);

      const pr = await ProductListView(id, db).catch(() => ({}));
      setProductList(pr?.Product || []);

      const res = await View_PromotionList(id, db).catch(() => ({}));
      const list = (res?.Promotion || []).reverse();
      setAllPromos(list);

      if (list.length) {
        const keys = Object.keys(list[0]).filter((k) => !OMIT_KEYS.includes(k));
        setPromoTypeOptions([...new Set(keys)]);
      } else {
        setPromoTypeOptions([]);
      }

      const base = makePromoColumns({
        permissions,
        onEdit: handleEditPromoRow,
        onDelete: handleDeletePromoRow,
      });
      setAllCols(base);
      const saved = localStorage.getItem("PromotionalActivity");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setVisibleCols(
            Array.isArray(parsed) && parsed.length ? parsed : base
          );
        } catch {
          setVisibleCols(base);
        }
      } else setVisibleCols(base);
    } finally {
      setLoading(false);
    }
  };

  /* master */
  const handleParentSubmit = (e) => {
    e.preventDefault();
    const value = JSON.parse(localStorage.getItem("SuperadminIdByMaster"));
    if (!value) return;
    const [id, db] = String(value).split(" ");
    loadBootstrap(id, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  /* Activity CRUD */
  const daysBetween = (s, e) => {
    if (!s || !e) return 0;
    const a = new Date(s);
    const b = new Date(e);
    return Math.ceil(Math.abs(b - a) / (1000 * 60 * 60 * 24)) + 1;
  };
  const onActChange = (e) =>
    setActForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submitActivity = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData"));
    const payload = {
      ActivityName: actForm.ActivityName,
      ActivityType: actForm.ActivityType,
      FromDate: actForm.FromDate,
      ToDate: actForm.ToDate,
      NoOfDays: daysBetween(actForm.FromDate, actForm.ToDate),
      status: actForm.status || "Active",
      database: user?.database,
    };
    try {
      if (actForm.editActivity && actForm.id) {
        await _Put(Update_Activity, actForm.id, payload);
        swal("Success", "Updated Successfully");
      } else {
        await _PostSave(SaveActivity, payload);
        swal("Success", "Created Successfully");
      }
      const a = await _Get(ActivityList, user?.database).catch(() => ({
        Activity: [],
      }));
      setActivityList(a?.Activity || []);
      setActForm({
        ActivityName: "",
        ActivityType: "",
        FromDate: "",
        ToDate: "",
        status: "Active",
        id: null,
        editActivity: false,
      });
      setActModal(false);
    } catch {
      swal("Error", "Something went wrong");
    }
  };
  const deleteActivity = async (row) => {
    try {
      await _Delete(Delete_Activity, row?._id);
      setActivityList((list) => list.filter((x) => x?._id !== row?._id));
      if (selectedActivity?._id === row?._id) setSelectedActivity(null);
    } catch {}
  };

  /* promos list derive */
  const recomputePromoRows = (typ, products) => {
    if (!typ || typ === "NA") {
      setPromoRows([]);
      return;
    }
    const filtered = allPromos.filter(
      (ele) => Array.isArray(ele[typ]) && ele[typ].length
    );
    let out = [];
    if (typ === "percentageWise" || typ === "amountWise") {
      out = filtered.map((ele) => {
        const val = ele[typ][0];
        return {
          ...val,
          U_id: val?._id,
          main_id: ele?._id,
          status: ele?.status,
          ...(ele?.activityId || {}),
        };
      });
    } else if (typ === "productWise") {
      out = filtered.flatMap((ele) =>
        ele[typ].map((item) => {
          const product =
            products.find((p) => p?._id === item?.productId) || null;
          const freeProduct =
            products.find((p) => p?._id === item?.freeProduct) || null;
          return {
            ...item,
            U_id: item?._id,
            main_id: ele?._id,
            Product: product,
            freeProductName: freeProduct,
            status: ele?.status,
            ...(ele?.activityId || {}),
          };
        })
      );
    }
    setPromoRows(out);
    setPage(1);
  };
  useEffect(() => {
    recomputePromoRows(promoType, productList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoType, allPromos, productList]);

  /* list search + paging */
  const filteredPromoRows = useMemo(() => {
    if (!promoSearch.trim()) return promoRows;
    const q = promoSearch.toLowerCase();
    const cols = visibleCols.filter((c) => c.id !== "actions");
    return promoRows.filter((r) =>
      cols.some((c) => {
        const v = c.accessor ? c.accessor(r) : r[c.id];
        return (
          v !== undefined && v !== null && String(v).toLowerCase().includes(q)
        );
      })
    );
  }, [promoRows, promoSearch, visibleCols]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPromoRows.length / pageSize)
  );
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPromoRows.slice(start, start + pageSize);
  }, [filteredPromoRows, page, pageSize]);

  /* list actions */
  const handleEditPromoRow = (row) => {
    navigate(
      `/app/ajgroup/account/EditPromotionalActivity/${row?.main_id}/${row?.U_id}`,
      {
        state: { data: row, key: row?.ActivityName, type: "Edit" },
      }
    );
  };
  const handleDeletePromoRow = (row) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (v) => {
      if (v === "delete") {
        try {
          await _Delete(DeleteOne_Promotion, row?.main_id);
          setPromoRows((prev) => prev.filter((r) => r !== row));
        } catch {}
      }
    });
  };

  /* column picker */
  const [colModal, setColModal] = useState(false);
  const toggleCols = () => setColModal((s) => !s);
  const addCols = () => {
    const merged = [
      ...visibleCols,
      ...bucketCols.filter((b) => !visibleCols.some((c) => c.id === b.id)),
    ];
    setVisibleCols(merged);
  };
  const removeCols = () =>
    setVisibleCols((cols) =>
      cols.filter((c) => !bucketCols.some((b) => b.id === c.id))
    );
  const removeOne = (col) =>
    setVisibleCols((cols) => cols.filter((c) => c.id !== col.id));
  const moveUp = () => {
    if (focusIndex < 1) return;
    const next = [...visibleCols];
    const [m] = next.splice(focusIndex, 1);
    next.splice(focusIndex - 1, 0, m);
    setVisibleCols(next);
    setFocusIndex((i) => i - 1);
  };
  const moveDown = () => {
    if (focusIndex < 0 || focusIndex >= visibleCols.length - 1) return;
    const next = [...visibleCols];
    const [m] = next.splice(focusIndex, 1);
    next.splice(focusIndex + 1, 0, m);
    setVisibleCols(next);
    setFocusIndex((i) => i + 1);
  };
  const saveCols = () => {
    localStorage.setItem("PromotionalActivity", JSON.stringify(visibleCols));
    setColModal(false);
  };

  /* exports */
  const buildCSV = () => rowsToCSV(filteredPromoRows, visibleCols);
  const exportPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "PromotionalActivity");
  };
  const exportCSV = () => downloadCSV(buildCSV());
  const exportXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "PromotionalActivity");
  };

  /* submit promo */
  const submitPromotion = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData"));
    if (!selectedActivity?._id) return swal("Select an Activity first.");

    try {
      let payload = {
        activityId: selectedActivity?._id,
        status: promoStatus,
        database: user?.database,
        created_by: user?._id,
      };

      if (discountType === "Percentage Wise") {
        if (!totalAmount || !percentOff)
          return swal("Enter total amount and % discount.");
        payload.percentageWise = [
          {
            totalAmount: +totalAmount,
            percentageDiscount: +percentOff,
            status: promoStatus,
            database: user?.database,
          },
        ];
      } else if (discountType === "Amount Wise") {
        if (!totalAmount || !flatAmount)
          return swal("Enter total amount and flat discount.");
        payload.amountWise = [
          {
            totalAmount: +totalAmount,
            percentageAmount: +flatAmount,
            status: promoStatus,
            database: user?.database,
          },
        ];
      } else if (discountType === "Product Wise") {
        const normalized = productRows
          .filter((r) => r.productId && r.targetQty)
          .map((r) => ({
            productId: r.productId,
            targetQty: +r.targetQty || 0,
            discountAmount: r.discountAmount ? +r.discountAmount : undefined,
            discountPercentage: r.discountPercentage
              ? +r.discountPercentage
              : undefined,
            freeProductQty: r.freeProductQty ? +r.freeProductQty : undefined,
            freeProduct: r.freeProduct || undefined,
            database: user?.database,
          }));
        if (!normalized.length)
          return swal("Add at least one valid product row.");
        payload.productWise = normalized;
      } else {
        return swal("Choose a Discount Type.");
      }

      await SavePromotionsActivity(payload);
      swal("Success", "Promotion Submitted Successfully");

      const res = await View_PromotionList(user?._id, user?.database);
      const list = (res?.Promotion || []).reverse();
      setAllPromos(list);
      setPromoType(
        discountType === "Product Wise"
          ? "productWise"
          : discountType === "Amount Wise"
          ? "amountWise"
          : "percentageWise"
      );
    } catch {
      swal("Error", "Something went wrong");
    }
  };

  /* UI */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <style>{`
        .page-wrap { padding: 8px; }

        /* Tabs */
        .nav-tabs .nav-link { padding: 6px 12px; font-weight: 600; font-size: 14px; }
        .nav-tabs .nav-link.active { color: #0a58ca; }

        .card-slim { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; }
        .title { font-weight: 600; text-transform: uppercase; font-size: 16px; margin: 6px 0 10px; }
        .muted { color: #64748b; font-size: 12px; }
        .btn-compact { height: 28px; padding: 0 10px; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
        .click { cursor: pointer; }
        .center { text-align: center; }

        /* Two-column layout in Promotional tab */
        .col-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 992px) { .col-wrap { grid-template-columns: 1fr; } }

        /* Builder (left) fixed width, outer scroll only */
        .builder-card {
          height: 550px;
          max-width: 700px;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          overflow-x: hidden;
        }

        /* Right list wider */
        .list-card {
          height: 550px;
          display: flex;
          min-width: 1000px;
          flex-direction: column;
          overflow: hidden;
        }
        .list-body { overflow: auto; }

        /* Generic table */
        .table { width: 100%; border-collapse: collapse; }
        .table th, .table td { padding: 8px; border: 1px solid #e5e7eb; font-size: 13px; text-align: center; white-space: nowrap; }
        .table th { background: #f8fafc; font-weight: 600; }

        /* Product-wise: keep dropdown visible; no inner scroll */
        .no-scroll-table { table-layout: fixed; width: 100%; }
        .no-scroll-table th, .no-scroll-table td { overflow: hidden; text-overflow: ellipsis; }
        .no-scroll-table .multiselect-container { overflow: visible !important; }
        .no-scroll-table .optionContainer { position: absolute; z-index: 9999; max-height: 220px; overflow: auto; }
        .no-scroll-table .searchBox,
        .no-scroll-table .multiselect-container .searchBox input { height: 28px; min-height: 28px; font-size: 12px; }

        /* Grouped header (4 + 3) */
        .mart-table thead .group th { background: #eef2ff; font-weight: 700; }
      `}</style>

      {/* Breadcrumb + master */}
      <Row className="mt-1 mb-2">
        <Col md="6">
          <a href="/#/dashboard" style={{ color: "green", paddingLeft: 10 }}>
            Home
          </a>
          <span className="muted"> &nbsp;/&nbsp; Promotions</span>
        </Col>
        <Col md="6" className="center">
          {masterShow && (
            <SuperAdminUI
              onDropdownChange={handleDropdownChange}
              onSubmit={handleParentSubmit}
            />
          )}
        </Col>
      </Row>

      <Nav tabs>
        <NavItem>
          <NavLink
            className={tab === "promo" ? "active" : ""}
            onClick={() => setTab("promo")}
          >
            Promotional Activity
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={tab === "activity" ? "active" : ""}
            onClick={() => setTab("activity")}
          >
            Activity
          </NavLink>
        </NavItem>
      </Nav>

      {/* ================= PROMOTIONAL TAB ================= */}
      {tab === "promo" && (
        <div className="col-wrap mt-2">
          {/* LEFT: Promotional Builder */}
          <Card className="card-slim builder-card">
            <div className="title">Promotional Builder</div>

            <Form onSubmit={submitPromotion}>
              <Row className="align-items-end">
                <Col lg="6" md="6" sm="12" className="mb-2">
                  <Label
                    style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                  >
                    Select Activity
                  </Label>
                  <Input
                    type="select"
                    value={selectedActivity?._id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const sel =
                        activityList.find((a) => a?._id === id) || null;
                      setSelectedActivity(sel);
                    }}
                    style={{ height: 32, fontSize: 13 }}
                  >
                    <option value="">-- Select --</option>
                    {activityList.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.ActivityName} ({prettyDate(a.FromDate)} →{" "}
                        {prettyDate(a.ToDate)})
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col lg="3" md="3" sm="6" className="mb-2 center">
                  <Button
                    color="info"
                    className="btn-compact"
                    type="button"
                    onClick={() => {
                      setActForm({
                        ActivityName: "",
                        ActivityType: "",
                        FromDate: "",
                        ToDate: "",
                        status: "Active",
                        id: null,
                        editActivity: false,
                      });
                      setActModal(true);
                    }}
                  >
                    <FaPlus size={12} /> Activity
                  </Button>
                </Col>

                <Col lg="3" md="3" sm="6" className="mb-2 center">
                  <Button
                    color="secondary"
                    className="btn-compact"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </Button>
                </Col>
              </Row>

              {/* Selected activity pill */}
              <Row className="mt-1">
                <Col className="center">
                  {selectedActivity ? (
                    <div className="pill">
                      <span>
                        <b>Promo-Code:</b> {selectedActivity?.Code || "—"}
                      </span>
                      <span
                        title="Copy"
                        className="click"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedActivity?.Code || ""
                          );
                          swal("Copied!");
                        }}
                      >
                        <Copy size={16} />
                      </span>
                      <span>
                        <b>Name:</b> {selectedActivity?.ActivityName}
                      </span>
                      <span>
                        <b>From:</b> {prettyDate(selectedActivity?.FromDate)}
                      </span>
                      <span>
                        <b>To:</b> {prettyDate(selectedActivity?.ToDate)}
                      </span>
                      <span>
                        <b>Status:</b> {selectedActivity?.status || "—"}
                      </span>
                    </div>
                  ) : (
                    <div className="muted">
                      Select an activity to build promotions.
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="mt-2">
                <Col lg="4" md="4" sm="12" className="mb-2">
                  <Label
                    style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                  >
                    Discount Type
                  </Label>
                  <Input
                    type="select"
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    style={{ height: 32, fontSize: 13 }}
                  >
                    <option value="">-- Select --</option>
                    <option value="Percentage Wise">Percentage Wise</option>
                    <option value="Amount Wise">Amount Wise</option>
                    <option value="Product Wise">Product Wise</option>
                  </Input>
                </Col>

                <Col lg="8" md="8" sm="12" className="mb-2 center">
                  <Label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      marginBottom: 6,
                      display: "block",
                    }}
                  >
                    Status
                  </Label>
                  <Label className="me-2" style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="promo_status"
                      value="Active"
                      checked={promoStatus === "Active"}
                      onChange={() => setPromoStatus("Active")}
                      style={{ marginRight: 6 }}
                    />
                    Active
                  </Label>
                  <Label style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="promo_status"
                      value="Deactive"
                      checked={promoStatus === "Deactive"}
                      onChange={() => setPromoStatus("Deactive")}
                      style={{ marginRight: 6, marginLeft: 16 }}
                    />
                    Deactive
                  </Label>
                </Col>
              </Row>

              {/* Percentage Wise */}
              {discountType === "Percentage Wise" && (
                <Row className="mt-1">
                  <Col lg="4" md="4" sm="6" className="mb-2">
                    <Label
                      style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                    >
                      Total Amount
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(e.target.value)}
                      placeholder="e.g. 10000"
                      style={{ height: 32, fontSize: 13 }}
                    />
                  </Col>
                  <Col lg="4" md="4" sm="6" className="mb-2">
                    <Label
                      style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                    >
                      Discount %
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={percentOff}
                      onChange={(e) => setPercentOff(e.target.value)}
                      placeholder="e.g. 5"
                      style={{ height: 32, fontSize: 13 }}
                    />
                  </Col>
                </Row>
              )}

              {/* Amount Wise */}
              {discountType === "Amount Wise" && (
                <Row className="mt-1">
                  <Col lg="4" md="4" sm="6" className="mb-2">
                    <Label
                      style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                    >
                      Total Amount
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(e.target.value)}
                      placeholder="e.g. 10000"
                      style={{ height: 32, fontSize: 13 }}
                    />
                  </Col>
                  <Col lg="4" md="4" sm="6" className="mb-2">
                    <Label
                      style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                    >
                      Flat Discount (₹)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={flatAmount}
                      onChange={(e) => setFlatAmount(e.target.value)}
                      placeholder="e.g. 500"
                      style={{ height: 32, fontSize: 13 }}
                    />
                  </Col>
                </Row>
              )}

              {/* Product Wise (NEW smart "mart table": grouped 4 + 3) */}
              {discountType === "Product Wise" && (
                <>
                  <Table
                    bordered
                    className="no-scroll-table mart-table"
                    style={{ fontSize: 12, marginTop: 6 }}
                  >
                    <thead>
                      {/* group header */}
                      <tr className="group">
                        <th colSpan={4}>Item</th>
                        <th colSpan={3}>Discount & Action</th>
                      </tr>
                      {/* true columns */}
                      <tr>
                        <th style={{ minWidth: 220 }}>Product</th>
                        <th style={{ minWidth: 110 }}>Target</th>
                        <th style={{ minWidth: 220 }}>Free Product</th>
                        <th style={{ minWidth: 100 }}>Free Qty</th>
                        <th style={{ minWidth: 110 }}>Disc %</th>
                        <th style={{ minWidth: 110 }}>Disc ₹</th>
                        <th style={{ minWidth: 80 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productRows.map((r, idx) => (
                        <tr key={idx}>
                          <td>
                            <Multiselect
                              selectionLimit={1}
                              isObject
                              displayValue="Product_Title"
                              options={productList}
                              onSelect={(sl, si) =>
                                onSelectProduct(sl, si, idx, "productId")
                              }
                              onRemove={(sl, si) =>
                                onRemoveProduct(sl, si, idx, "productId")
                              }
                              placeholder="Select"
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={r.targetQty}
                              onChange={(e) =>
                                onRowChange(idx, "targetQty", e.target.value)
                              }
                              style={{ height: 32, fontSize: 12 }}
                            />
                          </td>
                          <td>
                            <Multiselect
                              selectionLimit={1}
                              isObject
                              displayValue="Product_Title"
                              options={productList}
                              onSelect={(sl, si) =>
                                onSelectProduct(sl, si, idx, "freeProduct")
                              }
                              onRemove={(sl, si) =>
                                onRemoveProduct(sl, si, idx, "freeProduct")
                              }
                              placeholder="(Optional)"
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={r.freeProductQty}
                              onChange={(e) =>
                                onRowChange(
                                  idx,
                                  "freeProductQty",
                                  e.target.value
                                )
                              }
                              style={{ height: 32, fontSize: 12 }}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={r.discountPercentage}
                              onChange={(e) =>
                                onRowChange(
                                  idx,
                                  "discountPercentage",
                                  e.target.value
                                )
                              }
                              style={{ height: 32, fontSize: 12 }}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={r.discountAmount}
                              onChange={(e) =>
                                onRowChange(
                                  idx,
                                  "discountAmount",
                                  e.target.value
                                )
                              }
                              style={{ height: 32, fontSize: 12 }}
                            />
                          </td>
                          <td className="center">
                            {idx > 0 ? (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeRow(idx)}
                              >
                                x
                              </Button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="center">
                    <Button color="primary" size="sm" onClick={addRow}>
                      + Add Row
                    </Button>
                  </div>
                </>
              )}

              <div className="center" style={{ marginTop: 10 }}>
                <Button color="primary" type="submit" className="btn-compact">
                  Submit Promotion
                </Button>
              </div>
            </Form>
          </Card>

          {/* RIGHT: Promotional List */}
          <Card className="card-slim list-card">
            <div className="title">Promotional List</div>

            <Row className="align-items-end">
              <Col lg="4" md="4" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Promotion Type
                </Label>
                <Input
                  type="select"
                  value={promoType}
                  onChange={(e) => setPromoType(e.target.value)}
                  style={{ height: 32, fontSize: 13 }}
                >
                  <option value="NA">-- Select Type --</option>
                  {promoTypeOptions.map((o) => (
                    <option
                      key={o}
                      value={o}
                      style={{ textTransform: "capitalize" }}
                    >
                      {o}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col lg="4" md="4" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Search
                </Label>
                <Input
                  placeholder="Type to filter…"
                  value={promoSearch}
                  onChange={(e) => {
                    setPromoSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: 32, fontSize: 13 }}
                />
              </Col>

              <Col lg="4" md="4" sm="12" className="mb-2 center">
                <Button
                  color="light"
                  className="btn-compact"
                  onClick={toggleCols}
                  title="Choose columns"
                >
                  <FaFilter size={14} /> Columns
                </Button>
                <span
                  onMouseEnter={onDownloadHover}
                  onMouseLeave={onDownloadHover}
                  style={{
                    display: "inline-block",
                    marginLeft: 10,
                    position: "relative",
                  }}
                >
                  <Button color="light" className="btn-compact">
                    <ImDownload size={14} /> Download
                  </Button>
                  {dlOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 30,
                        border: "1px solid #0a58ca",
                        background: "#fff",
                        zIndex: 5,
                      }}
                    >
                      <div
                        className="click center"
                        onClick={exportPDF}
                        style={{ padding: "6px 10px" }}
                      >
                        .PDF
                      </div>
                      <div
                        className="click center"
                        onClick={exportCSV}
                        style={{ padding: "6px 10px" }}
                      >
                        .CSV
                      </div>
                      <div
                        className="click center"
                        onClick={exportXLS}
                        style={{ padding: "6px 10px" }}
                      >
                        .XLS
                      </div>
                    </div>
                  )}
                </span>
              </Col>
            </Row>

            <div className="list-body" style={{ marginTop: 8 }}>
              <div style={{ overflowX: "auto" }}>
                <Table bordered className="table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th
                          key={c.id}
                          style={{ width: c.width ? c.width : "auto" }}
                        >
                          {c.headerName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, i) => (
                      <tr key={i}>
                        {visibleCols.map((c) => (
                          <td
                            key={c.id}
                            style={{ width: c.width ? c.width : "auto" }}
                          >
                            {c.cell
                              ? c.cell(row)
                              : c.accessor
                              ? c.accessor(row)
                              : row?.[c.id] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {pageRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={visibleCols.length}
                          className="muted center"
                        >
                          No data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <Row className="mt-1 align-items-center">
                <Col className="center" md="6" sm="12">
                  <Button
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <span className="mx-2">
                    Page {page} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </Col>
                <Col className="center" md="6" sm="12">
                  <span className="me-1">Rows:</span>
                  <Input
                    type="select"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{ width: 90, height: 32, display: "inline-block" }}
                  >
                    {[10, 15, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </div>
          </Card>
        </div>
      )}

      {/* ================= ACTIVITY TAB ================= */}
      {tab === "activity" && (
        <Card className="card-slim mt-2">
          <div className="title">Activity</div>
          <Row className="align-items-end">
            <Col lg="4" md="4" sm="12" className="mb-2">
              <Label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Search
              </Label>
              <Input
                placeholder="Search by Activity / Customer / Type"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
                style={{ height: 32, fontSize: 13 }}
              />
            </Col>
            <Col lg="8" md="8" sm="12" className="mb-2 center">
              <Button
                color="info"
                className="btn-compact"
                onClick={() => {
                  setActForm({
                    ActivityName: "",
                    ActivityType: "",
                    FromDate: "",
                    ToDate: "",
                    status: "Active",
                    id: null,
                    editActivity: false,
                  });
                  setActModal(true);
                }}
              >
                <FaPlus size={12} /> Add Activity
              </Button>
            </Col>
          </Row>

          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <Table bordered className="table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Activity</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.map((a, i) => (
                  <tr key={a?._id || i}>
                    <td>{i + 1}</td>
                    <td>{a?.ActivityName}</td>
                    <td>{a?.Code}</td>
                    <td>{a?.ActivityType}</td>
                    <td>{prettyDate(a?.FromDate)}</td>
                    <td>{prettyDate(a?.ToDate)}</td>
                    <td>{a?.status}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "center",
                        }}
                      >
                        <Edit
                          size={18}
                          color="green"
                          className="click"
                          title="Edit"
                          onClick={() => {
                            setActForm({
                              ActivityName: a?.ActivityName,
                              ActivityType: a?.ActivityType,
                              FromDate: prettyDate(a?.FromDate),
                              ToDate: prettyDate(a?.ToDate),
                              status: a?.status || "Active",
                              id: a?._id,
                              editActivity: true,
                            });
                            setActModal(true);
                          }}
                        />
                        <Trash2
                          size={18}
                          color="red"
                          className="click"
                          title="Delete"
                          onClick={() => deleteActivity(a)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredActivities.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted center">
                      No activities found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* ===== Column Picker Modal ===== */}
      <Modal
        isOpen={colModal}
        toggle={toggleCols}
        style={{ maxWidth: "980px" }}
      >
        <ModalHeader toggle={toggleCols}>Change Columns</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" className="mb-2">
              <h6>Available</h6>
              <div className="picker-panel">
                {allCols.map((c) => (
                  <div key={c.id} className="mt-1">
                    <label className="click" style={{ fontSize: 13 }}>
                      <input
                        type="checkbox"
                        className="me-1"
                        onChange={() => {
                          const exists = bucketCols.find((x) => x.id === c.id);
                          if (exists) {
                            const idx = bucketCols.findIndex(
                              (x) => x.id === c.id
                            );
                            if (idx > -1) bucketCols.splice(idx, 1);
                          } else {
                            bucketCols.push(c);
                          }
                        }}
                      />
                      {c.headerName}
                    </label>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg="2" md="2" sm="12" className="center mb-2">
              <div>
                <Button
                  color="primary"
                  outline
                  className="mb-1"
                  onClick={addCols}
                >
                  Add »
                </Button>
              </div>
              <div>
                <Button color="primary" outline onClick={removeCols}>
                  « Remove
                </Button>
              </div>
            </Col>
            <Col lg="6" md="6" sm="12" className="mb-2">
              <Row>
                <Col md="8">
                  <h6>Visible</h6>
                  <div className="picker-panel">
                    {visibleCols.map((c, i) => (
                      <div key={c.id} className="mt-1">
                        <div
                          className={`chip click ${
                            focusIndex === i ? "active" : ""
                          }`}
                          onClick={() => setFocusIndex(i)}
                        >
                          <IoMdRemoveCircleOutline
                            className="me-1 click"
                            size={18}
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeOne(c);
                            }}
                          />
                          {c.headerName}
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col
                  md="4"
                  className="center d-flex flex-column justify-content-center"
                >
                  <Button
                    color="primary"
                    outline
                    className="mb-1"
                    onClick={moveUp}
                  >
                    <BsFillArrowUpSquareFill />
                  </Button>
                  <Button color="primary" outline onClick={moveDown}>
                    <BsFillArrowDownSquareFill />
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="center">
              <Button color="primary" onClick={saveCols}>
                Submit
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* ===== Add/Edit Activity Modal ===== */}
      <Modal size="lg" isOpen={actModal} toggle={toggleAct}>
        <ModalHeader toggle={toggleAct}>
          {actForm.editActivity ? "Edit Activity" : "Add Activity"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={submitActivity}>
            <Row>
              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Activity Name
                </Label>
                <Input
                  required
                  type="text"
                  name="ActivityName"
                  value={actForm.ActivityName}
                  onChange={onActChange}
                />
              </Col>
              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Start Date
                </Label>
                <Input
                  required
                  type="date"
                  name="FromDate"
                  value={actForm.FromDate}
                  onChange={onActChange}
                />
              </Col>
              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Activity Type
                </Label>
                <Input
                  type="select"
                  name="ActivityType"
                  value={actForm.ActivityType}
                  onChange={onActChange}
                >
                  <option value="">Select Type</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="HalfYearly">HalfYearly</option>
                  <option value="Yearly">Yearly</option>
                </Input>
              </Col>
              <Col lg="6" md="6" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  End Date
                </Label>
                <Input
                  required
                  type="date"
                  name="ToDate"
                  value={actForm.ToDate}
                  onChange={onActChange}
                />
              </Col>
              <Col lg="12" md="12" sm="12" className="mb-2">
                <Label
                  style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}
                >
                  Status
                </Label>
                <div>
                  <Label style={{ fontSize: 12 }} className="me-2">
                    <input
                      type="radio"
                      name="status"
                      value="Active"
                      checked={(actForm.status || "Active") === "Active"}
                      onChange={onActChange}
                      style={{ marginRight: 6 }}
                    />
                    Active
                  </Label>
                  <Label style={{ fontSize: 12 }}>
                    <input
                      type="radio"
                      name="status"
                      value="Deactive"
                      checked={actForm.status === "Deactive"}
                      onChange={onActChange}
                      style={{ marginRight: 6, marginLeft: 12 }}
                    />
                    Deactive
                  </Label>
                </div>
              </Col>
            </Row>
            <div className="center">
              <Button
                color="primary"
                type="submit"
                className="btn-compact mt-1"
              >
                Submit
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
}
